// TEMPLATE — disclosure copy below is a starting point.
// State licenses and carrier disclosures are admin-editable from /admin/legal.
// A licensed attorney with insurance + financial services experience should
// review the seeded sections (3 and 4) before launch.
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { supabase } from "@/integrations/supabase/client";

type StateLicense = {
  id: string;
  state_code: string;
  state_name: string;
  license_number: string | null;
  license_lines: string[];
  effective_date: string | null;
  expiration_date: string | null;
  disclosure_text: string | null;
  contact_required: string | null;
  sort_order: number;
};

type CarrierDisclosure = {
  id: string;
  carrier_name: string;
  carrier_slug: string;
  carrier_logo_url: string | null;
  relationship_type: "appointed" | "contracted" | "non-contracted" | "referral-only";
  disclosure_text: string;
  product_lines: string[];
  states: string[];
  sort_order: number;
};

export const Route = createFileRoute("/disclosures")({
  head: () => ({
    meta: [
      { title: "Disclosures & Licenses — Currency Thoughts" },
      {
        name: "description",
        content:
          "State licenses, carrier relationships, and compensation disclosures for Currency Thoughts LLC.",
      },
      { property: "og:title", content: "Disclosures & Licenses — Currency Thoughts" },
      {
        property: "og:description",
        content:
          "Transparency about who we are, where we operate, and how we are compensated.",
      },
    ],
  }),
  component: DisclosuresPage,
});

function DisclosuresPage() {
  const [licenses, setLicenses] = useState<StateLicense[]>([]);
  const [carriers, setCarriers] = useState<CarrierDisclosure[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [l, c] = await Promise.all([
        supabase
          .from("state_licenses")
          .select(
            "id, state_code, state_name, license_number, license_lines, effective_date, expiration_date, disclosure_text, contact_required, sort_order",
          )
          .eq("active", true)
          .order("sort_order")
          .order("state_name"),
        supabase
          .from("carrier_disclosures")
          .select(
            "id, carrier_name, carrier_slug, carrier_logo_url, relationship_type, disclosure_text, product_lines, states, sort_order",
          )
          .eq("active", true)
          .order("sort_order")
          .order("carrier_name"),
      ]);
      if (cancelled) return;
      if (l.data) setLicenses(l.data as StateLicense[]);
      if (c.data) setCarriers(c.data as CarrierDisclosure[]);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-screen bg-cream-50">
      <Navbar />

      {/* Hero */}
      <section className="bg-cream-50 px-5 pb-16 pt-28 md:px-10 md:pt-32">
        <div className="mx-auto max-w-5xl text-center">
          <Eyebrow align="center">Disclosures &amp; Licenses</Eyebrow>
          <h1 className="mt-6 font-serif text-4xl font-bold text-navy-900 md:text-5xl">
            Disclosures &amp; Licenses
          </h1>
          <p className="mx-auto mt-4 max-w-2xl font-sans text-[15px] leading-relaxed text-charcoal-500">
            Transparency about who we are, where we operate, and how we are
            compensated.
          </p>
        </div>
      </section>

      {/* SECTION 1 — State Licenses */}
      <section className="bg-white px-5 py-16 md:px-10">
        <div className="mx-auto max-w-7xl">
          <Eyebrow>Licensed in</Eyebrow>
          <h2 className="mt-4 font-serif text-3xl font-bold text-navy-900 md:text-4xl">
            Where we are licensed.
          </h2>

          {licenses.length === 0 ? (
            <p className="mt-8 font-sans text-sm text-charcoal-500">
              State license information will appear here once published.
            </p>
          ) : (
            <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
              {licenses.map((l) => (
                <LicenseCard key={l.id} license={l} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* SECTION 2 — Carrier Relationships */}
      <section className="bg-cream-50 px-5 py-16 md:px-10">
        <div className="mx-auto max-w-7xl">
          <Eyebrow>Carriers</Eyebrow>
          <h2 className="mt-4 font-serif text-3xl font-bold text-navy-900 md:text-4xl">
            Our carrier relationships.
          </h2>
          <p className="mt-4 max-w-[720px] font-sans text-[14px] leading-relaxed text-charcoal-900">
            Currency Thoughts is an independent insurance agency. We maintain
            appointments with multiple carriers to give families honest product
            options. Below is current disclosure of those relationships.
          </p>

          {carriers.length === 0 ? (
            <p className="mt-8 font-sans text-sm text-charcoal-500">
              Carrier relationship information will appear here once published.
            </p>
          ) : (
            <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
              {carriers.map((c) => (
                <CarrierCard key={c.id} carrier={c} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* SECTION 3 — Educational Disclosure */}
      <section className="bg-white px-5 py-12 md:px-10">
        <div className="mx-auto max-w-[720px]">
          <h3 className="font-serif text-2xl font-semibold text-navy-900 md:text-3xl">
            About our educational content
          </h3>
          <p className="mt-4 font-sans text-[15px] leading-relaxed text-charcoal-900">
            All content published by Currency Thoughts — including the Academy,
            the Family Library, the Family Snapshot, and the Legacy Score — is
            for educational purposes only. It is not legal, tax, or investment
            advice. Nothing on this platform constitutes a guarantee of any
            policy benefit or outcome. Speak with a licensed professional and
            review carrier-issued documents before making any purchase decision.
          </p>
        </div>
      </section>

      {/* SECTION 4 — Compensation Disclosure */}
      <section className="bg-cream-50 px-5 py-12 md:px-10">
        <div className="mx-auto max-w-[720px]">
          <h3 className="font-serif text-2xl font-semibold text-navy-900 md:text-3xl">
            How our strategists are compensated
          </h3>
          <p className="mt-4 font-sans text-[15px] leading-relaxed text-charcoal-900">
            Currency Thoughts strategists are independent contractors who receive
            commission compensation from insurance carriers when a family
            purchases a policy. They may also receive overrides on team
            production. Specific commission rates vary by carrier and product.
            You may request our compensation disclosure in writing at any time.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function LicenseCard({ license }: { license: StateLicense }) {
  const exp = license.expiration_date
    ? new Date(license.expiration_date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : null;
  return (
    <article className="border-t-2 border-navy-800 bg-white p-5 shadow-[0_2px_12px_-8px_rgba(168,139,92,0.18)]">
      <h3 className="font-serif text-[22px] font-semibold text-navy-800">
        {license.state_name}
      </h3>
      {license.license_number && (
        <p className="mt-2 font-sans text-[13px] text-charcoal-500">
          License #{license.license_number}
        </p>
      )}
      {license.license_lines.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-1.5">
          {license.license_lines.map((line) => (
            <li
              key={line}
              className="border border-bronze-400/60 bg-bronze-400/10 px-2 py-0.5 font-sans text-[11px] uppercase tracking-[0.12em] text-bronze-600"
            >
              {line}
            </li>
          ))}
        </ul>
      )}
      {exp && (
        <p className="mt-3 font-sans text-[11px] uppercase tracking-[0.15em] text-charcoal-500">
          Expires: {exp}
        </p>
      )}
      {license.disclosure_text && (
        <p className="mt-3 font-sans text-[12px] italic leading-relaxed text-charcoal-500">
          {license.disclosure_text}
        </p>
      )}
      {license.contact_required && (
        <p className="mt-2 font-sans text-[12px] leading-relaxed text-charcoal-500">
          {license.contact_required}
        </p>
      )}
    </article>
  );
}

function CarrierCard({ carrier }: { carrier: CarrierDisclosure }) {
  const rel = carrier.relationship_type;
  const pillStyles =
    rel === "appointed"
      ? "border border-bronze-400 bg-bronze-400/15 text-bronze-600"
      : rel === "contracted"
      ? "border border-bronze-600 bg-bronze-600/10 text-bronze-600"
      : rel === "non-contracted"
      ? "border border-navy-700 bg-transparent text-navy-700"
      : "border border-charcoal-500/40 bg-transparent text-charcoal-500";

  return (
    <article className="flex h-full flex-col border border-bronze-600/30 bg-white p-5 shadow-[0_2px_12px_-8px_rgba(168,139,92,0.18)]">
      <div className="flex items-center gap-4">
        {carrier.carrier_logo_url ? (
          <img
            src={carrier.carrier_logo_url}
            alt={`${carrier.carrier_name} logo`}
            className="h-10 w-20 rounded-sm border border-bronze-600/40 bg-white object-contain p-1"
            loading="lazy"
          />
        ) : (
          <div
            className="flex h-10 w-20 items-center justify-center rounded-sm border border-bronze-600/60 bg-navy-800 font-serif text-[11px] uppercase tracking-[0.18em] text-cream-50"
            aria-hidden
          >
            {carrier.carrier_name.slice(0, 2)}
          </div>
        )}
        <div>
          <h3 className="font-serif text-[20px] font-semibold text-navy-900">
            {carrier.carrier_name}
          </h3>
          <span
            className={`mt-1 inline-block px-2 py-0.5 font-sans text-[10px] font-semibold uppercase tracking-[0.18em] ${pillStyles}`}
          >
            {rel.replace("-", " ")}
          </span>
        </div>
      </div>

      {carrier.product_lines.length > 0 && (
        <ul className="mt-4 flex flex-wrap gap-1.5">
          {carrier.product_lines.map((line) => (
            <li
              key={line}
              className="border border-bronze-400/60 bg-bronze-400/10 px-2 py-0.5 font-sans text-[11px] uppercase tracking-[0.12em] text-bronze-600"
            >
              {line}
            </li>
          ))}
        </ul>
      )}

      <p className="mt-3 font-sans text-[12px] uppercase tracking-[0.15em] text-charcoal-500">
        {carrier.states.length === 0
          ? "All licensed states"
          : carrier.states.join(", ")}
      </p>

      {carrier.disclosure_text && (
        <p className="mt-4 font-sans text-[13px] leading-relaxed text-charcoal-900">
          {carrier.disclosure_text}
        </p>
      )}
    </article>
  );
}